import { useEffect, useState } from "react";
import { api } from "../lib/api.ts";
import { useBrands } from "../lib/brand.tsx";
import { Icon } from "../lib/icons.tsx";
import { AWARENESS_LEVELS, TRAFFIC_TEMPS, sectionLabel, type SalesPageInputs, type SalesPageOutput } from "@shared/salespage.ts";

const FIELDS: { key: keyof SalesPageInputs; label: string; area?: boolean }[] = [
  { key: "product", label: "สินค้า/บริการ" },
  { key: "price", label: "ราคา" },
  { key: "target", label: "กลุ่มเป้าหมาย" },
  { key: "conversionGoal", label: "เป้าหมาย (เช่น ทักแชต/ซื้อ)" },
  { key: "usp", label: "จุดต่าง (USP)", area: true },
  { key: "offer", label: "ข้อเสนอ (Offer)", area: true },
  { key: "pain", label: "Pain", area: true },
  { key: "desire", label: "Desire", area: true },
  { key: "objection", label: "Objection หลัก", area: true },
  { key: "proof", label: "หลักฐาน/ผลลัพธ์ (Proof)", area: true },
  { key: "authority", label: "ความน่าเชื่อถือ (Authority)" },
  { key: "guarantee", label: "การรับประกัน" },
];

const TABS = [
  { id: "l1", label: "1 · Research" },
  { id: "l2", label: "2 · Strategy" },
  { id: "l3", label: "3-4 · Copy" },
  { id: "l5", label: "5 · Visual" },
  { id: "qc", label: "QC" },
];

export default function SalesPageEngine() {
  const { activeBrand } = useBrands();
  const [inp, setInp] = useState<SalesPageInputs>({ awarenessLevel: "problem_aware", trafficTemperature: "cold" });
  const [out, setOut] = useState<SalesPageOutput | null>(null);
  const [isMock, setIsMock] = useState(true);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [tab, setTab] = useState("l1");
  const [history, setHistory] = useState<any[]>([]);
  const [saved, setSaved] = useState(false);

  useEffect(() => { load(); }, [activeBrand?.id]);
  async function load() { if (!activeBrand) return setHistory([]); const r = await api.get<{ pages: any[] }>(`/sales-page?brandId=${activeBrand.id}`); setHistory(r.pages); }
  const set = (k: keyof SalesPageInputs, v: string) => setInp((p) => ({ ...p, [k]: v }));

  async function generate() {
    if (!activeBrand) { setErr("เลือกแบรนด์ก่อน"); return; }
    setErr(null); setLoading(true); setOut(null); setSaved(false);
    try {
      const r = await api.post<any>("/sales-page/generate", { brandId: activeBrand.id, inputs: inp });
      if (r.error === "ai_unparsable") { setErr("AI ตอบไม่เป็นรูปแบบ ลองใหม่"); return; }
      setOut(r.output); setIsMock(r.isMock); setTab("l1"); load();
    } catch (e: any) { setErr("ผิดพลาด: " + (e?.message || "")); }
    finally { setLoading(false); }
  }
  async function open(id: string) { setLoading(true); try { const r = await api.get<{ page: any }>(`/sales-page/${id}`); setOut(r.page.data); setIsMock(false); setTab("l1"); } finally { setLoading(false); } }
  async function saveLearning() {
    if (!activeBrand || !out) return;
    await api.post("/learnings", { brandId: activeBrand.id, type: "sales_page", title: `Sales Page: ${out.layer2_strategy.bigIdea.slice(0, 50)}`, sourceModule: "sales-page", confidence: "medium" });
    setSaved(true);
  }

  return (
    <div className="page">
      <div className="page__head mh-spread">
        <div>
          <div className="page__title">Sales Page Engine</div>
          <div className="page__subtitle">{activeBrand ? <>บริบท: <b>{activeBrand.name}</b> — 5-Layer: Research → Strategy → Architecture → Copy → Visual</> : "เลือกแบรนด์ก่อน"}</div>
        </div>
        <Icon name="FileText" size={22} />
      </div>

      <div className="mh-card card-pad" style={{ marginBottom: 18 }}>
        <div className="form-grid" style={{ marginBottom: 12 }}>
          <div className="mh-field" style={{ margin: 0 }}>
            <label className="mh-label">ระดับการรับรู้ (Awareness)</label>
            <select className="mh-select" value={inp.awarenessLevel} onChange={(e) => set("awarenessLevel", e.target.value)}>
              {AWARENESS_LEVELS.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
            </select>
          </div>
          <div className="mh-field" style={{ margin: 0 }}>
            <label className="mh-label">อุณหภูมิ Traffic</label>
            <select className="mh-select" value={inp.trafficTemperature} onChange={(e) => set("trafficTemperature", e.target.value)}>
              {TRAFFIC_TEMPS.map((tt) => <option key={tt.id} value={tt.id}>{tt.label}</option>)}
            </select>
          </div>
        </div>
        <div className="mh-help" style={{ marginBottom: 12 }}>ช่องว่างจะดึงจาก Brand Brain ให้อัตโนมัติ · ข้อมูลที่ไม่มี ระบบจะทำเป็น "สมมติฐาน" และบอกให้ยืนยันก่อนใช้</div>
        <div className="form-grid">
          {FIELDS.map((f) => (
            <div className="mh-field" style={{ margin: 0, gridColumn: f.area ? "1 / -1" : undefined }} key={f.key}>
              <label className="mh-label" style={{ fontSize: 12.5 }}>{f.label}</label>
              {f.area
                ? <textarea className="mh-textarea" style={{ minHeight: 56 }} value={(inp[f.key] as string) || ""} onChange={(e) => set(f.key, e.target.value)} />
                : <input className="mh-input" value={(inp[f.key] as string) || ""} onChange={(e) => set(f.key, e.target.value)} />}
            </div>
          ))}
        </div>
        <div style={{ marginTop: 16, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <button className="mh-btn mh-btn--primary mh-btn--lg" onClick={generate} disabled={loading}>
            {loading ? <span className="spinner" /> : <><Icon name="FileText" size={18} /> สร้าง Sales Page</>}
          </button>
          {history.length > 0 && (
            <select className="mh-select" style={{ width: "auto" }} defaultValue="" onChange={(e) => e.target.value && open(e.target.value)}>
              <option value="">📁 เพจเดิม ({history.length})</option>
              {history.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
            </select>
          )}
          {err && <span className="mh-pill mh-pill--problem">{err}</span>}
        </div>
      </div>

      {loading && <div className="empty"><span className="spinner spinner--dark" /><p className="mh-sec">กำลังสร้าง 5 Layers...</p></div>}

      {out && (
        <>
          {isMock && <div className="mock-banner"><Icon name="Wrench" size={18} /><div>โครง + คอปปี้จาก <b>Mock</b> — ใส่ Claude Key เพื่อคอปปี้ขายระดับมืออาชีพจริง</div></div>}

          <div className="mh-spread" style={{ marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {TABS.map((t) => (
                <button key={t.id} className={"mh-btn mh-btn--sm " + (tab === t.id ? "mh-btn--primary" : "mh-btn--ghost")} onClick={() => setTab(t.id)}>{t.label}</button>
              ))}
            </div>
            <button className="mh-btn mh-btn--subtle mh-btn--sm" onClick={saveLearning} disabled={saved}>{saved ? "บันทึกแล้ว ✓" : <><Icon name="Save" size={15} /> Save as Learning</>}</button>
          </div>

          {tab === "l1" && <Layer1 out={out} />}
          {tab === "l2" && <Layer2 out={out} />}
          {tab === "l3" && <Copy out={out} />}
          {tab === "l5" && <Visual out={out} />}
          {tab === "qc" && <QC out={out} />}
        </>
      )}
    </div>
  );
}

function KV({ k, v }: { k: string; v: string }) {
  return <div style={{ marginBottom: 10 }}><div className="mh-muted" style={{ fontSize: 12.5 }}>{k}</div><div style={{ fontSize: 14.5 }}>{v}</div></div>;
}

function Layer1({ out }: { out: SalesPageOutput }) {
  const r = out.layer1_research;
  return (
    <div className="mh-card card-pad">
      <KV k="Customer Insight" v={r.customerInsight} />
      <div className="form-grid" style={{ marginTop: 8 }}>
        <div><b style={{ fontSize: 13.5 }}>ขุด Pain</b><ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>{r.painDug.map((p, i) => <li key={i}>{p}</li>)}</ul></div>
        <div><b style={{ fontSize: 13.5 }}>ขุด Desire</b><ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>{r.desireDug.map((p, i) => <li key={i}>{p}</li>)}</ul></div>
      </div>
      <div style={{ marginTop: 14 }}><b style={{ fontSize: 13.5 }}>Objection → แมพลง Section</b>
        <div style={{ display: "grid", gap: 6, marginTop: 6 }}>
          {r.objectionsDug.map((o, i) => <div key={i} className="mh-row" style={{ gap: 8 }}><span>{o.text}</span><Icon name="ArrowRight" size={14} /><span className="mh-pill mh-pill--testing">{sectionLabel(o.mappedSection)}</span></div>)}
        </div>
      </div>
      {r.hypotheses.length > 0 && <div className="mock-banner" style={{ marginTop: 14 }}><Icon name="Scale" size={16} /><div><b>สมมติฐาน (ยังไม่ยืนยัน):</b><ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>{r.hypotheses.map((h, i) => <li key={i}>{h}</li>)}</ul></div></div>}
    </div>
  );
}

function Layer2({ out }: { out: SalesPageOutput }) {
  const s = out.layer2_strategy;
  return (
    <>
      <div className="mh-card card-pad" style={{ marginBottom: 14 }}>
        <div className="form-grid">
          <div><KV k="Customer Truth" v={s.customerTruth} /><KV k="Core Problem" v={s.coreProblem} /><KV k="Enemy / Old Way" v={s.enemyOldWay} /><KV k="New Opportunity" v={s.newOpportunity} /></div>
          <div><KV k="Big Idea 💡" v={s.bigIdea} /><KV k="Core Promise" v={s.corePromise} /><KV k="Unique Mechanism" v={s.uniqueMechanism} /><KV k="Buying Trigger" v={s.buyingTrigger} /></div>
        </div>
      </div>
      <div className="mh-card card-pad" style={{ marginBottom: 14 }}>
        <h3 style={{ fontSize: 14, marginBottom: 10 }}>มุมขาย (Angles) — ไฮไลต์ = Top 3</h3>
        <div style={{ display: "grid", gap: 8 }}>
          {s.angles.map((a, i) => (
            <div key={i} className="mh-spread" style={{ padding: "8px 12px", borderRadius: 8, background: a.top ? "var(--blue-050)" : "var(--gray-050)", border: a.top ? "1px solid var(--blue-100)" : "1px solid transparent" }}>
              <div><b>{a.name}</b> {a.top && <span className="mh-pill mh-pill--winner" style={{ marginLeft: 6 }}>Top</span>}<div className="mh-sec" style={{ fontSize: 13 }}>{a.why}</div></div>
              <div style={{ fontWeight: 800, color: "var(--blue-600)" }}>{a.score}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="mh-card card-pad">
        <h3 style={{ fontSize: 14, marginBottom: 10 }}>พาดหัว (Headlines) — ไฮไลต์ = Top 5</h3>
        <div style={{ display: "grid", gap: 6 }}>
          {s.headlines.map((h, i) => (
            <div key={i} className="mh-spread" style={{ padding: "7px 12px", borderRadius: 8, background: h.top ? "var(--green-050)" : "transparent", borderBottom: "1px solid var(--border)" }}>
              <span>{h.top && "⭐ "}{h.text}</span>
              <span className="mh-pill mh-pill--neutral">{h.category}</span>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

function Copy({ out }: { out: SalesPageOutput }) {
  return (
    <>
      <div className="mh-card card-pad" style={{ marginBottom: 14, background: "var(--blue-050)", borderColor: "var(--blue-100)" }}>
        <b>เหตุผลของ Flow:</b> <span className="mh-sec">{out.layer3_architecture.flowRationale}</span>
      </div>
      <div style={{ display: "grid", gap: 12 }}>
        {out.sections.map((sec, i) => (
          <div className="mh-card" key={i} style={{ overflow: "hidden" }}>
            <div style={{ padding: "10px 16px", background: "var(--navy-800)", color: "#fff", display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ opacity: 0.7, fontWeight: 700 }}>{i + 1}</span>
              <b>{sectionLabel(sec.type)}</b>
              <span style={{ marginLeft: "auto", fontSize: 12, color: "var(--text-on-navy-muted)" }}>{sec.purpose}</span>
            </div>
            <div className="card-pad">
              <div style={{ whiteSpace: "pre-wrap", fontSize: 14.5, lineHeight: 1.65, marginBottom: 10 }}>{sec.copy}</div>
              <div className="form-grid" style={{ gap: "6px 16px" }}>
                <div className="mh-muted" style={{ fontSize: 12.5 }}>ก่อนอ่าน: <span style={{ color: "var(--text-secondary)" }}>{sec.readerBefore}</span></div>
                <div className="mh-muted" style={{ fontSize: 12.5 }}>หลังอ่าน: <span style={{ color: "var(--text-secondary)" }}>{sec.readerAfter}</span></div>
                <div className="mh-muted" style={{ fontSize: 12.5 }}>Proof: <span style={{ color: "var(--text-secondary)" }}>{sec.proofNeeded}</span></div>
                <div className="mh-muted" style={{ fontSize: 12.5 }}>แก้ Objection: <span style={{ color: "var(--text-secondary)" }}>{sec.objectionAddressed}</span></div>
              </div>
              <div style={{ marginTop: 8, fontSize: 12.5, color: "var(--blue-600)" }}>→ {sec.ctaTransition}</div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function Visual({ out }: { out: SalesPageOutput }) {
  return (
    <div className="mh-card card-pad">
      <KV k="ทิศทางภาพรวม (Overall)" v={out.layer5_visual_overall} />
      <h3 style={{ fontSize: 14, margin: "10px 0" }}>ทิศทางภาพต่อ Section</h3>
      <div style={{ display: "grid", gap: 8 }}>
        {out.sections.map((sec, i) => (
          <div key={i} className="mh-row" style={{ gap: 10, padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
            <span className="mh-pill mh-pill--neutral" style={{ minWidth: 120 }}>{sectionLabel(sec.type)}</span>
            <span style={{ fontSize: 13.5 }}>{sec.visualDirection}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function QC({ out }: { out: SalesPageOutput }) {
  const V: Record<string, string> = { pass: "mh-pill--winner", warn: "mh-pill--watch", fail: "mh-pill--problem" };
  return (
    <div className="mh-card card-pad">
      <div className="mh-spread" style={{ marginBottom: 14 }}>
        <h3 style={{ fontSize: 15 }}>Conversion QC</h3>
        <div style={{ fontSize: 26, fontWeight: 800, color: out.qc.score >= 80 ? "var(--green-600)" : out.qc.score >= 60 ? "var(--amber-600)" : "var(--red-600)" }}>{out.qc.score}%</div>
      </div>
      <div style={{ display: "grid", gap: 8 }}>
        {out.qc.checks.map((c, i) => (
          <div key={i} className="mh-spread" style={{ padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
            <div><b style={{ fontSize: 14 }}>{c.item}</b><div className="mh-sec" style={{ fontSize: 13 }}>{c.note}</div></div>
            <span className={"mh-pill " + V[c.verdict]}>{c.verdict}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
